import React, { useState, useEffect } from 'react';
import { 
  Users, Shield, Activity, Clock, Database, RotateCcw, Trash2,
  ChevronDown, ChevronUp, Eye, UserCog, AlertTriangle, Check, Pencil,
  Plus, Download, RefreshCw, Calendar, FileText, Settings as SettingsIcon
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '../components/ui/collapsible';
import { adminAPI, authAPI } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

const formatDate = (dateString) => {
  if (!dateString) return '-';
  const date = new Date(dateString);
  return date.toLocaleString('nl-BE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const formatRelativeTime = (dateString) => {
  if (!dateString) return 'Nooit';
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);
  
  if (diffMins < 1) return 'Zojuist';
  if (diffMins < 60) return `${diffMins} min geleden`;
  if (diffHours < 24) return `${diffHours} uur geleden`;
  if (diffDays < 7) return `${diffDays} dagen geleden`;
  return formatDate(dateString);
};

const actionLabels = {
  create: 'Aangemaakt',
  update: 'Bijgewerkt',
  delete: 'Verwijderd',
  view: 'Bekeken',
  login: 'Ingelogd',
  export: 'Geëxporteerd',
  restore: 'Hersteld'
};

const actionColors = {
  create: 'bg-green-500/20 text-green-400 border-green-500/30',
  update: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
  delete: 'bg-red-500/20 text-red-400 border-red-500/30',
  view: 'bg-gray-500/20 text-gray-400 border-gray-500/30',
  login: 'bg-purple-500/20 text-purple-400 border-purple-500/30',
  export: 'bg-amber-500/20 text-amber-400 border-amber-500/30',
  restore: 'bg-cyan-500/20 text-cyan-400 border-cyan-500/30'
};

const entityLabels = {
  customer: 'Klant',
  property: 'Adres',
  installation: 'Installatie',
  appointment: 'Afspraak',
  maintenance: 'Onderhoud',
  cleaning: 'Reiniging',
  snapshot: 'Snapshot',
  user: 'Gebruiker'
};

const roleLabels = {
  admin: 'Administrator',
  technician: 'Technicus',
  sales: 'Verkoper',
  cleaner: 'Install/Clean'
};

const roleColors = {
  admin: 'bg-red-500/20 text-red-400 border-red-500/30',
  technician: 'bg-amber-500/20 text-amber-400 border-amber-500/30',
  sales: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
  cleaner: 'bg-purple-500/20 text-purple-400 border-purple-500/30'
};

const AdminPanel = () => {
  const { user: currentUser } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('users');
  const [loading, setLoading] = useState(true);
  
  // Data
  const [users, setUsers] = useState([]);
  const [activities, setActivities] = useState([]);
  const [snapshots, setSnapshots] = useState([]);
  const [stats, setStats] = useState(null);
  
  // Filters
  const [activityFilter, setActivityFilter] = useState({ action: '', entity_type: '', user_id: '' });
  const [selectedUser, setSelectedUser] = useState(null);
  const [userActivities, setUserActivities] = useState([]);
  
  // Dialogs
  const [userDetailOpen, setUserDetailOpen] = useState(false);
  const [createSnapshotOpen, setCreateSnapshotOpen] = useState(false);
  const [snapshotDescription, setSnapshotDescription] = useState('');
  const [restoreConfirmOpen, setRestoreConfirmOpen] = useState(false);
  const [snapshotToRestore, setSnapshotToRestore] = useState(null);
  const [deleteUserConfirm, setDeleteUserConfirm] = useState(null);
  const [roleChangeUser, setRoleChangeUser] = useState(null);
  const [newRole, setNewRole] = useState('');
  
  // Gebruiker bewerken (naam, email)
  const [editUser, setEditUser] = useState(null);
  const [editForm, setEditForm] = useState({ first_name: '', last_name: '', email: '' });
  const [savingUser, setSavingUser] = useState(false);
  
  // Create user state
  const [createUserOpen, setCreateUserOpen] = useState(false);
  const [newUser, setNewUser] = useState({
    first_name: '',
    last_name: '',
    email: '',
    password: '',
    role: 'technician'
  });
  const [creatingUser, setCreatingUser] = useState(false);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [usersRes, activitiesRes, snapshotsRes, statsRes] = await Promise.all([
        adminAPI.getUsers(),
        adminAPI.getActivity({ limit: 200 }),
        adminAPI.getSnapshots(),
        adminAPI.getStats()
      ]);
      setUsers(usersRes.data);
      setActivities(activitiesRes.data);
      setSnapshots(snapshotsRes.data);
      setStats(statsRes.data);
    } catch (error) {
      toast.error('Kon admin data niet laden');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleViewUserActivity = async (user) => {
    setSelectedUser(user);
    try {
      const res = await adminAPI.getUserActivity(user.id);
      setUserActivities(res.data);
      setUserDetailOpen(true);
    } catch (error) {
      toast.error('Kon activiteiten niet laden');
    }
  };

  const handleChangeRole = async () => {
    if (!roleChangeUser || !newRole) return;
    try {
      await adminAPI.updateUserRole(roleChangeUser.id, newRole);
      toast.success('Rol bijgewerkt');
      setRoleChangeUser(null);
      setNewRole('');
      fetchData();
    } catch (error) {
      toast.error('Kon rol niet wijzigen');
    }
  };

  const openEditUser = (user) => {
    setEditUser(user);
    setEditForm({
      first_name: user.first_name || '',
      last_name: user.last_name || '',
      email: user.email || ''
    });
  };

  const handleSaveUser = async () => {
    if (!editForm.first_name.trim() || !editForm.last_name.trim() || !editForm.email.trim()) {
      toast.error('Vul naam en email in');
      return;
    }
    setSavingUser(true);
    try {
      await adminAPI.updateUser(editUser.id, editForm);
      toast.success('Gebruiker bijgewerkt');
      setEditUser(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Kon gebruiker niet bijwerken');
    } finally {
      setSavingUser(false);
    }
  };

  const handleDeleteUser = async () => {
    if (!deleteUserConfirm) return;
    try {
      await adminAPI.deleteUser(deleteUserConfirm.id);
      toast.success('Gebruiker verwijderd');
      setDeleteUserConfirm(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Kon gebruiker niet verwijderen');
    }
  };

  const handleCreateUser = async () => {
    if (!newUser.first_name || !newUser.last_name || !newUser.email || !newUser.password) {
      toast.error('Vul alle verplichte velden in');
      return;
    }
    if (newUser.password.length < 8 || !/[A-Za-z]/.test(newUser.password) || !/\d/.test(newUser.password)) {
      toast.error('Wachtwoord moet minimaal 8 tekens bevatten met minstens één letter en één cijfer');
      return;
    }
    setCreatingUser(true);
    try {
      await authAPI.register(newUser);
      toast.success(`Gebruiker ${newUser.first_name} ${newUser.last_name} aangemaakt`);
      setCreateUserOpen(false);
      setNewUser({
        first_name: '',
        last_name: '',
        email: '',
        password: '',
        role: 'technician'
      });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Kon gebruiker niet aanmaken');
    } finally {
      setCreatingUser(false);
    }
  };

  const handleCreateSnapshot = async () => {
    try {
      const res = await adminAPI.createSnapshot(snapshotDescription || 'Handmatige snapshot');
      toast.success(`Snapshot aangemaakt met ${Object.values(res.data.stats).reduce((a, b) => a + b, 0)} records`);
      setCreateSnapshotOpen(false);
      setSnapshotDescription('');
      fetchData();
    } catch (error) {
      toast.error('Kon snapshot niet aanmaken');
    }
  };

  const handleRestoreSnapshot = async () => {
    if (!snapshotToRestore) return;
    try {
      const res = await adminAPI.restoreSnapshot(snapshotToRestore.id);
      toast.success('Data hersteld! Backup gemaakt met ID: ' + res.data.backup_id);
      setRestoreConfirmOpen(false);
      setSnapshotToRestore(null);
      fetchData();
    } catch (error) {
      toast.error('Kon data niet herstellen');
    }
  };

  const handleDeleteSnapshot = async (snapshotId) => {
    try {
      await adminAPI.deleteSnapshot(snapshotId);
      toast.success('Snapshot verwijderd');
      fetchData();
    } catch (error) {
      toast.error('Kon snapshot niet verwijderen');
    }
  };

  const filteredActivities = activities.filter(a => {
    if (activityFilter.action && a.action !== activityFilter.action) return false;
    if (activityFilter.entity_type && a.entity_type !== activityFilter.entity_type) return false;
    if (activityFilter.user_id && a.user_id !== activityFilter.user_id) return false;
    return true;
  });

  return (
    <div className="space-y-6 animate-fade-in" data-testid="admin-panel">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-4xl font-bold tracking-tight">Admin Panel</h1>
          <p className="text-muted-foreground mt-1">
            Gebruikersbeheer, activiteiten en systeemherstel
          </p>
        </div>
        <Button onClick={fetchData} variant="outline" className="gap-2">
          <RefreshCw className="w-4 h-4" />
          Vernieuwen
        </Button>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="p-4 flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-primary/20 flex items-center justify-center">
                <Users className="w-5 h-5 text-primary" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats.users_count}</p>
                <p className="text-xs text-muted-foreground">Gebruikers</p>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-green-500/20 flex items-center justify-center">
                <Activity className="w-5 h-5 text-green-400" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats.activities_today}</p>
                <p className="text-xs text-muted-foreground">Acties vandaag</p>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-blue-500/20 flex items-center justify-center">
                <Database className="w-5 h-5 text-blue-400" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats.snapshots_count}</p>
                <p className="text-xs text-muted-foreground">Snapshots</p>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-amber-500/20 flex items-center justify-center">
                <Clock className="w-5 h-5 text-amber-400" />
              </div>
              <div>
                <p className="text-2xl font-bold">{activities.length}</p>
                <p className="text-xs text-muted-foreground">Totaal logs</p>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="users" className="gap-2">
            <Users className="w-4 h-4" />
            Gebruikers
          </TabsTrigger>
          <TabsTrigger value="activity" className="gap-2">
            <Activity className="w-4 h-4" />
            Activiteiten
          </TabsTrigger>
          <TabsTrigger value="snapshots" className="gap-2">
            <Database className="w-4 h-4" />
            Snapshots
          </TabsTrigger>
        </TabsList>

        {/* Users Tab */}
        <TabsContent value="users" className="mt-6">
          <Card>
            <CardHeader>
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div>
                  <CardTitle>Gebruikers</CardTitle>
                  <CardDescription>Beheer gebruikersaccounts en rollen</CardDescription>
                </div>
                <Button onClick={() => setCreateUserOpen(true)} className="gap-2">
                  <Plus className="w-4 h-4" />
                  Nieuwe Gebruiker
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="space-y-2">
                  {[1, 2, 3].map(i => (
                    <div key={i} className="h-16 animate-pulse bg-muted rounded" />
                  ))}
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Naam</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead>Rol</TableHead>
                      <TableHead>Aangemaakt</TableHead>
                      <TableHead>Laatste activiteit</TableHead>
                      <TableHead>Acties</TableHead>
                      <TableHead className="text-right">Beheer</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {users.map(user => (
                      <TableRow key={user.id}>
                        <TableCell className="font-medium">
                          {user.first_name} {user.last_name}
                          {user.id === currentUser?.id && (
                            <Badge variant="outline" className="ml-2 text-xs">Jij</Badge>
                          )}
                        </TableCell>
                        <TableCell>{user.email}</TableCell>
                        <TableCell>
                          <Badge variant="outline" className={roleColors[user.role]}>
                            {roleLabels[user.role]}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-muted-foreground text-sm">
                          {formatDate(user.created_at)}
                        </TableCell>
                        <TableCell className="text-muted-foreground text-sm">
                          {formatRelativeTime(user.last_activity)}
                        </TableCell>
                        <TableCell>
                          <Badge variant="secondary">{user.activity_count || 0}</Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-2">
                            <Button 
                              variant="ghost" 
                              size="icon"
                              onClick={() => handleViewUserActivity(user)}
                            >
                              <Eye className="w-4 h-4" />
                            </Button>
                            <Button 
                              variant="ghost" 
                              size="icon"
                              onClick={() => openEditUser(user)}
                              title="Gegevens bewerken"
                              data-testid={`edit-user-${user.id}`}
                            >
                              <Pencil className="w-4 h-4" />
                            </Button>
                            <Button 
                              variant="ghost" 
                              size="icon"
                              onClick={() => { setRoleChangeUser(user); setNewRole(user.role); }}
                              disabled={user.id === currentUser?.id}
                            >
                              <UserCog className="w-4 h-4" />
                            </Button>
                            <Button 
                              variant="ghost" 
                              size="icon"
                              onClick={() => setDeleteUserConfirm(user)}
                              disabled={user.id === currentUser?.id}
                              className="text-destructive hover:text-destructive"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Activity Tab */}
        <TabsContent value="activity" className="mt-6">
          <Card>
            <CardHeader>
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div>
                  <CardTitle>Activiteitenlogboek</CardTitle>
                  <CardDescription>Alle acties op het platform</CardDescription>
                </div>
                <div className="flex flex-wrap gap-2">
                  <Select value={activityFilter.action || 'all'} onValueChange={(v) => setActivityFilter(prev => ({ ...prev, action: v === 'all' ? '' : v }))}>
                    <SelectTrigger className="w-[140px]">
                      <SelectValue placeholder="Alle acties" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Alle acties</SelectItem>
                      <SelectItem value="create">Aangemaakt</SelectItem>
                      <SelectItem value="update">Bijgewerkt</SelectItem>
                      <SelectItem value="delete">Verwijderd</SelectItem>
                      <SelectItem value="login">Ingelogd</SelectItem>
                      <SelectItem value="export">Geëxporteerd</SelectItem>
                      <SelectItem value="restore">Hersteld</SelectItem>
                    </SelectContent>
                  </Select>
                  <Select value={activityFilter.entity_type || 'all'} onValueChange={(v) => setActivityFilter(prev => ({ ...prev, entity_type: v === 'all' ? '' : v }))}>
                    <SelectTrigger className="w-[140px]">
                      <SelectValue placeholder="Alle types" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Alle types</SelectItem>
                      <SelectItem value="customer">Klant</SelectItem>
                      <SelectItem value="property">Adres</SelectItem>
                      <SelectItem value="installation">Installatie</SelectItem>
                      <SelectItem value="appointment">Afspraak</SelectItem>
                      <SelectItem value="snapshot">Snapshot</SelectItem>
                      <SelectItem value="user">Gebruiker</SelectItem>
                    </SelectContent>
                  </Select>
                  <Select value={activityFilter.user_id || 'all'} onValueChange={(v) => setActivityFilter(prev => ({ ...prev, user_id: v === 'all' ? '' : v }))}>
                    <SelectTrigger className="w-[160px]">
                      <SelectValue placeholder="Alle gebruikers" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Alle gebruikers</SelectItem>
                      {users.map(u => (
                        <SelectItem key={u.id} value={u.id}>
                          {u.first_name} {u.last_name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-2 max-h-[600px] overflow-y-auto">
                {filteredActivities.length === 0 ? (
                  <p className="text-center text-muted-foreground py-8">Geen activiteiten gevonden</p>
                ) : (
                  filteredActivities.map(activity => {
                    // Build navigation function based on entity type
                    const getNavigationPath = () => {
                      if (!activity.entity_id) return null;
                      const paths = {
                        customer: `/customers?highlight=${activity.entity_id}`,
                        property: `/customers`,
                        installation: `/installations?highlight=${activity.entity_id}`,
                        cleaning: `/cleaning?highlight=${activity.entity_id}`,
                        offerte: `/offertes?highlight=${activity.entity_id}`,
                        factuur: `/facturen?highlight=${activity.entity_id}`,
                        aanvraag: `/aanvragen?highlight=${activity.entity_id}`,
                        product: `/producten`,
                        installation_offerte: `/offertes?tab=installatie&highlight=${activity.entity_id}`,
                        installation_factuur: `/facturen?tab=installatie&highlight=${activity.entity_id}`,
                        ticket: `/tickets?highlight=${activity.entity_id}`,
                      };
                      return paths[activity.entity_type] || null;
                    };
                    
                    const navPath = getNavigationPath();
                    const details = activity.details || {};
                    
                    // Build details display string
                    const getDetailsSummary = () => {
                      const parts = [];
                      if (details.name) parts.push(details.name);
                      if (details.offerte_number) parts.push(`#${details.offerte_number}`);
                      if (details.factuur_number) parts.push(`#${details.factuur_number}`);
                      if (details.status) parts.push(`Status: ${details.status}`);
                      if (details.source) parts.push(`Bron: ${details.source}`);
                      if (details.permanent) parts.push('(permanent verwijderd)');
                      return parts.join(' • ');
                    };
                    
                    const detailsSummary = getDetailsSummary();
                    
                    return (
                      <div 
                        key={activity.id} 
                        className={`p-3 rounded-lg bg-muted/30 hover:bg-muted/50 transition-colors ${navPath ? 'cursor-pointer' : ''}`}
                        onClick={() => {
                          if (navPath) {
                            navigate(navPath);
                          }
                        }}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <Badge variant="outline" className={actionColors[activity.action]}>
                              {actionLabels[activity.action] || activity.action}
                            </Badge>
                            <div>
                              <p className="text-sm">
                                <span className="font-medium">{activity.user_name}</span>
                                {' '}{actionLabels[activity.action]?.toLowerCase() || activity.action}{' '}
                                <span className="text-muted-foreground">
                                  {entityLabels[activity.entity_type] || activity.entity_type}
                                </span>
                                {navPath && <Eye className="w-3 h-3 ml-2 inline text-muted-foreground" />}
                              </p>
                              {detailsSummary && (
                                <p className="text-xs text-muted-foreground mt-1">
                                  {detailsSummary}
                                </p>
                              )}
                            </div>
                          </div>
                          <span className="text-xs text-muted-foreground whitespace-nowrap">
                            {formatRelativeTime(activity.timestamp)}
                          </span>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Snapshots Tab */}
        <TabsContent value="snapshots" className="mt-6">
          <Card>
            <CardHeader>
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div>
                  <CardTitle>Data Snapshots</CardTitle>
                  <CardDescription>Maak backups en herstel eerdere versies</CardDescription>
                </div>
                <Button onClick={() => setCreateSnapshotOpen(true)} className="gap-2">
                  <Plus className="w-4 h-4" />
                  Nieuwe Snapshot
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {snapshots.length === 0 ? (
                <div className="text-center py-12">
                  <Database className="w-12 h-12 mx-auto mb-4 text-muted-foreground opacity-50" />
                  <p className="text-muted-foreground">Geen snapshots beschikbaar</p>
                  <p className="text-sm text-muted-foreground mt-1">
                    Maak een snapshot om een backup van je data te maken
                  </p>
                </div>
              ) : (
                <div className="space-y-3">
                  {snapshots.map(snapshot => (
                    <Card key={snapshot.id} className="bg-muted/30">
                      <CardContent className="p-4">
                        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                          <div className="flex items-start gap-3">
                            <div className="w-10 h-10 rounded-lg bg-blue-500/20 flex items-center justify-center flex-shrink-0">
                              <Database className="w-5 h-5 text-blue-400" />
                            </div>
                            <div>
                              <p className="font-medium">{snapshot.description}</p>
                              <p className="text-sm text-muted-foreground">
                                {formatDate(snapshot.created_at)} door {snapshot.created_by}
                              </p>
                              <div className="flex flex-wrap gap-2 mt-2">
                                <Badge variant="secondary" className="text-xs">
                                  {snapshot.stats?.customers || 0} klanten
                                </Badge>
                                <Badge variant="secondary" className="text-xs">
                                  {snapshot.stats?.properties || 0} adressen
                                </Badge>
                                <Badge variant="secondary" className="text-xs">
                                  {snapshot.stats?.installations || 0} installaties
                                </Badge>
                                <Badge variant="secondary" className="text-xs">
                                  {snapshot.stats?.appointments || 0} afspraken
                                </Badge>
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <Button 
                              variant="outline" 
                              size="sm"
                              onClick={() => { setSnapshotToRestore(snapshot); setRestoreConfirmOpen(true); }}
                              className="gap-1"
                            >
                              <RotateCcw className="w-4 h-4" />
                              Herstellen
                            </Button>
                            <Button 
                              variant="ghost" 
                              size="icon"
                              onClick={() => handleDeleteSnapshot(snapshot.id)}
                              className="text-destructive hover:text-destructive"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* User Activity Dialog */}
      <Dialog open={userDetailOpen} onOpenChange={setUserDetailOpen}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              Activiteiten van {selectedUser?.first_name} {selectedUser?.last_name}
            </DialogTitle>
            <DialogDescription>
              {userActivities.length} gelogde acties
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 mt-4">
            {userActivities.length === 0 ? (
              <p className="text-center text-muted-foreground py-8">Geen activiteiten gevonden</p>
            ) : (
              userActivities.map(activity => {
                // Build navigation function based on entity type
                const getNavigationPath = () => {
                  if (!activity.entity_id) return null;
                  const paths = {
                    customer: `/customers?highlight=${activity.entity_id}`,
                    property: `/customers`,
                    installation: `/installations?highlight=${activity.entity_id}`,
                    cleaning: `/cleaning?highlight=${activity.entity_id}`,
                    offerte: `/offertes?highlight=${activity.entity_id}`,
                    factuur: `/facturen?highlight=${activity.entity_id}`,
                    aanvraag: `/aanvragen?highlight=${activity.entity_id}`,
                    product: `/producten`,
                    installation_offerte: `/offertes?tab=installatie&highlight=${activity.entity_id}`,
                    installation_factuur: `/facturen?tab=installatie&highlight=${activity.entity_id}`,
                    ticket: `/tickets?highlight=${activity.entity_id}`,
                  };
                  return paths[activity.entity_type] || null;
                };
                
                const navPath = getNavigationPath();
                const details = activity.details || {};
                const hasDetails = Object.keys(details).length > 0;
                
                // Build details display string
                const getDetailsSummary = () => {
                  const parts = [];
                  if (details.name) parts.push(details.name);
                  if (details.offerte_number) parts.push(`#${details.offerte_number}`);
                  if (details.factuur_number) parts.push(`#${details.factuur_number}`);
                  if (details.status) parts.push(`Status: ${details.status}`);
                  if (details.source) parts.push(`Bron: ${details.source}`);
                  if (details.permanent) parts.push('(permanent verwijderd)');
                  return parts.join(' • ');
                };
                
                return (
                  <div 
                    key={activity.id} 
                    className={`p-3 rounded-lg bg-muted/30 ${navPath ? 'cursor-pointer hover:bg-muted/50 transition-colors' : ''}`}
                    onClick={() => {
                      if (navPath) {
                        setUserDetailOpen(false);
                        navigate(navPath);
                      }
                    }}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <Badge variant="outline" className={actionColors[activity.action]}>
                          {actionLabels[activity.action] || activity.action}
                        </Badge>
                        <span className="text-sm font-medium">
                          {entityLabels[activity.entity_type] || activity.entity_type}
                        </span>
                        {navPath && (
                          <Eye className="w-3 h-3 text-muted-foreground" />
                        )}
                      </div>
                      <span className="text-xs text-muted-foreground">
                        {formatDate(activity.timestamp)}
                      </span>
                    </div>
                    {hasDetails && (
                      <p className="text-xs text-muted-foreground mt-1.5 pl-2 border-l-2 border-muted">
                        {getDetailsSummary()}
                      </p>
                    )}
                  </div>
                );
              })
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Create Snapshot Dialog */}
      <Dialog open={createSnapshotOpen} onOpenChange={setCreateSnapshotOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Nieuwe Snapshot Maken</DialogTitle>
            <DialogDescription>
              Maak een backup van alle huidige data. Je kunt later naar deze snapshot terugkeren.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">Beschrijving</label>
              <Input
                placeholder="bv. Voor grote update, Einde maand backup..."
                value={snapshotDescription}
                onChange={(e) => setSnapshotDescription(e.target.value)}
              />
            </div>
          </div>
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setCreateSnapshotOpen(false)}>
              Annuleren
            </Button>
            <Button onClick={handleCreateSnapshot}>
              <Database className="w-4 h-4 mr-2" />
              Snapshot Maken
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Restore Confirm Dialog */}
      <AlertDialog open={restoreConfirmOpen} onOpenChange={setRestoreConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-500" />
              Data Herstellen?
            </AlertDialogTitle>
            <AlertDialogDescription>
              Dit zal alle huidige data vervangen door de data uit de snapshot "{snapshotToRestore?.description}".
              <br /><br />
              <strong>Let op:</strong> Er wordt automatisch een backup gemaakt van de huidige staat voordat het herstel plaatsvindt.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuleren</AlertDialogCancel>
            <AlertDialogAction onClick={handleRestoreSnapshot} className="bg-amber-600 hover:bg-amber-700">
              <RotateCcw className="w-4 h-4 mr-2" />
              Herstellen
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Change Role Dialog */}
      {/* Gebruiker Bewerken Dialog */}
      <Dialog open={!!editUser} onOpenChange={() => setEditUser(null)}>
        <DialogContent data-testid="edit-user-dialog">
          <DialogHeader>
            <DialogTitle>Gebruiker Bewerken</DialogTitle>
            <DialogDescription>
              Wijzig de gegevens van {editUser?.first_name} {editUser?.last_name}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Voornaam *</Label>
                <Input
                  value={editForm.first_name}
                  onChange={(e) => setEditForm(prev => ({ ...prev, first_name: e.target.value }))}
                  data-testid="edit-user-firstname"
                />
              </div>
              <div className="space-y-2">
                <Label>Achternaam *</Label>
                <Input
                  value={editForm.last_name}
                  onChange={(e) => setEditForm(prev => ({ ...prev, last_name: e.target.value }))}
                  data-testid="edit-user-lastname"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Email *</Label>
              <Input
                type="email"
                value={editForm.email}
                onChange={(e) => setEditForm(prev => ({ ...prev, email: e.target.value }))}
                data-testid="edit-user-email"
              />
              <p className="text-xs text-muted-foreground">Let op: dit is ook het inlog-emailadres</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditUser(null)}>Annuleren</Button>
            <Button onClick={handleSaveUser} disabled={savingUser} data-testid="save-user-btn">
              {savingUser ? 'Opslaan...' : 'Opslaan'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Role Change Dialog */}
      <Dialog open={!!roleChangeUser} onOpenChange={() => setRoleChangeUser(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Rol Wijzigen</DialogTitle>
            <DialogDescription>
              Wijzig de rol van {roleChangeUser?.first_name} {roleChangeUser?.last_name}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <Select value={newRole} onValueChange={setNewRole}>
              <SelectTrigger>
                <SelectValue placeholder="Selecteer rol" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="admin">Administrator</SelectItem>
                <SelectItem value="technician">Technicus</SelectItem>
                <SelectItem value="sales">Verkoper</SelectItem>
                <SelectItem value="cleaner">Install/Clean</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setRoleChangeUser(null)}>
              Annuleren
            </Button>
            <Button onClick={handleChangeRole}>
              Rol Opslaan
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete User Confirm */}
      <AlertDialog open={!!deleteUserConfirm} onOpenChange={() => setDeleteUserConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Gebruiker Verwijderen?</AlertDialogTitle>
            <AlertDialogDescription>
              Weet je zeker dat je {deleteUserConfirm?.first_name} {deleteUserConfirm?.last_name} wilt verwijderen?
              Dit kan niet ongedaan worden gemaakt.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuleren</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteUser} className="bg-destructive hover:bg-destructive/90">
              Verwijderen
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Create User Dialog */}
      <Dialog open={createUserOpen} onOpenChange={setCreateUserOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Nieuwe Gebruiker Aanmaken</DialogTitle>
            <DialogDescription>
              Maak een nieuw account aan. De gebruiker kan hiermee inloggen.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <label className="text-sm font-medium">Voornaam *</label>
                <Input
                  placeholder="Jan"
                  value={newUser.first_name}
                  onChange={(e) => setNewUser(prev => ({ ...prev, first_name: e.target.value }))}
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">Achternaam *</label>
                <Input
                  placeholder="Jansen"
                  value={newUser.last_name}
                  onChange={(e) => setNewUser(prev => ({ ...prev, last_name: e.target.value }))}
                />
              </div>
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Email *</label>
              <Input
                type="email"
                placeholder="jan.jansen@bedrijf.be"
                value={newUser.email}
                onChange={(e) => setNewUser(prev => ({ ...prev, email: e.target.value }))}
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Wachtwoord *</label>
              <Input
                type="password"
                placeholder="Minimaal 8 tekens, letter + cijfer"
                value={newUser.password}
                onChange={(e) => setNewUser(prev => ({ ...prev, password: e.target.value }))}
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Rol</label>
              <Select value={newUser.role} onValueChange={(v) => setNewUser(prev => ({ ...prev, role: v }))}>
                <SelectTrigger>
                  <SelectValue placeholder="Selecteer rol" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="admin">Administrator - Volledige toegang</SelectItem>
                  <SelectItem value="technician">Technicus - Onderhoud & installaties</SelectItem>
                  <SelectItem value="sales">Verkoper - Klanten & kansen</SelectItem>
                  <SelectItem value="cleaner">Install/Clean - Installaties & Reinigingen</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setCreateUserOpen(false)}>
              Annuleren
            </Button>
            <Button onClick={handleCreateUser} disabled={creatingUser}>
              {creatingUser ? 'Aanmaken...' : 'Gebruiker Aanmaken'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminPanel;
